import * as React from "react";
import {PropsWithChildren, useEffect} from "react";
import {useTranslation} from "react-i18next";
import Breadcrumb from "./Breadcrumb";
import styles from "../lib/ModalWindow/CartWrapperModal.module.scss";
import genericStyles from "../lib/GenericStyles.module.scss";
import {useCheckoutViewContext} from "./CheckoutContextProvider/CheckoutViewContextProvider";
import Finished from "./steps/Finished/Finished";
import Login from "./steps/Login/Login";
import Payment from "./steps/Payment/Payment";
import Shipping from "./steps/Shipping/Shipping";
import Summary from "./steps/Summary/Summary";
import {ICustomerData, IProductTrackingData, IPurchaseSummaryInformation, Tracking} from "../../tracking/tracking";
import {ICartPosition, ICartData} from "../../typescript-library";
import {useCheckoutContext} from "./CheckoutContextProvider/CheckoutContextProvider";
import AddressStep from "./steps/AddressStep/AddressStep";
import {useCartContext} from "../cart/CartContextProvider/CartContextProvider";
import {updateUrlParams} from "../cart/CartIcon/CartIconPortal";
import {Payments} from "../lib/payments/PaymentController";
import {restoreCart} from "../lib/payments/decideCartContent";

const Checkout: React.FC = () => {
  const {t} = useTranslation(["errorcodes", "translations"]);
  const {step} = useCheckoutViewContext();
  const {checkout} = useCheckoutContext();
  const {cart, setIsIntoCheckout, setIsCartVisible, viewState} = useCartContext();

  useEffect(() => {
    const productData = prepareTrackingProductData(checkout.cartData);

    if (step !== CheckoutStep.FINISHED) {
      Tracking.sendCheckoutStepEvent(step, productData);
    } else if (step === CheckoutStep.FINISHED && checkout.orderId) {
      const shippingCostsPos = checkout.cartData.positions.find<ICartPosition>(
        (pos): pos is ICartPosition => pos.type === "Shipping",
      );

      const shippingCostGross = Number(shippingCostsPos?.amount.amount ?? 0);
      const shippingCostTaxes = Number(shippingCostsPos?.tax.amount.amount ?? 0);

      const voucherCode = checkout.cartData.positions.find<ICartPosition>(
        (pos): pos is ICartPosition => pos.type === "Voucher" && !!pos.reference?.voucher?.isValid,
      )?.reference?.voucher?.code;

      const promotionId = checkout.cartData.promotion?.id ?? -1;

      const summaryData: IPurchaseSummaryInformation = {
        id: checkout.orderId,
        affiliation: promotionId,
        coupon: voucherCode ?? "",
        revenue: checkout.cartData.value.gross.amount,
        shipping: (shippingCostGross - shippingCostTaxes).toFixed(2),
        tax: checkout.cartData.value.tax
          .reduce((accumulator, currentTax) => accumulator + parseFloat(currentTax.amount.amount), 0.0)
          .toFixed(2),
      };

      const customerData: ICustomerData = {
        Birthday: checkout.invoiceAddress?.dateOfBirth ?? "",
        Email: checkout.invoiceAddress?.email ?? "",
        Salutation:
          checkout.invoiceAddress?.gender === "F" ? "Mrs." : checkout.invoiceAddress?.gender === null ? "Mx." : "Mr.",
      };

      Tracking.sendPurchaseEvent(summaryData, productData, customerData);
    }
    scrollToTop();
  }, [step]);

  const showBreadCrumbs =
    step !== CheckoutStep.FINISHED &&
    step !== CheckoutStep.CARTERRORS &&
    !(checkout.paymentId === Payments.PaypalExpressBraintree && viewState.paypalInfos) &&
    !(checkout.paymentId === Payments.GooglePay && viewState.gPayData) &&
    !(checkout.paymentId === Payments.ApplePay && viewState.appleData);

  const BackButton = () => {
    if (step === CheckoutStep.FINISHED) {
      // In FINISHED step you can click the logo
      return null;
    }

    const fromPDP = (viewState.appleData || viewState.paypalInfos)?.place === "pdp";

    return (
      <button
        type="button"
        className={styles.backButton}
        onClick={() => {
          setIsIntoCheckout(false, checkout);
          if (fromPDP) {
            setIsCartVisible(false);
            updateUrlParams(null);
            void restoreCart(cart);
          } else {
            updateUrlParams("cart");
          }
        }}
      >
        <i className={"icon icon-size--24 icon-left--blue"} />
        <span>{t(fromPDP ? "general.back" : "checkout.backToCart")}</span>
      </button>
    );
  };

  return (
    <>
      <div>
        <BackButton />
        {showBreadCrumbs && <Breadcrumb />}
        {step === CheckoutStep.CARTERRORS && <CheckoutError>{t("checkout.cartEmptyOrIncorrect")}</CheckoutError>}
        <Login isVisible={step === CheckoutStep.LOGIN} />
        <AddressStep isVisible={step === CheckoutStep.ADDRESS} />
        <Shipping isVisible={step === CheckoutStep.SHIPPING} />
        <Payment isVisible={step === CheckoutStep.PAYMENT} />
        {step === CheckoutStep.SUMMARY && <Summary />}
        <Finished isVisible={step === CheckoutStep.FINISHED} />
      </div>
    </>
  );
};
export default Checkout;

export const CheckoutError: React.FC<PropsWithChildren> = (props) => {
  const listOfErrors = React.Children.map(props.children, (child: string | JSX.Element) => {
    return !child ? undefined : <li>{child}</li>;
  });
  if (!(listOfErrors && listOfErrors.length)) {
    return null;
  }

  return (
    <div className={genericStyles.errorBlockMain} role="alert">
      <ul>{listOfErrors}</ul>
    </div>
  );
};

export function prepareTrackingProductData(cartData: ICartData) {
  const productData: IProductTrackingData[] = [];

  cartData.positions.forEach((position: ICartPosition) => {
    if (position.type === "Item" && position.item?.itemId) {
      productData.push({
        brand: position.item.manufacturer,
        id: position.item.itemId,
        name: position.text,
        price: position.price.amount,
        quantity: position.quantity,
      });
    }
  });

  return productData;
}

export enum CheckoutStep {
  CARTERRORS = "carterrors",
  LOGIN = "login",
  ADDRESS = "address",
  SHIPPING = "shipping",
  PAYMENT = "payment",
  SUMMARY = "summary",
  FINISHED = "finished",
}
export enum LoginMode {
  NEWCUSTOMER = "new",
  KNOWNCUSTOMER = "known",
  FACEBOOK = "facebook",
}
export enum PaymentMethod {
  PAYPAL = "PayPal",
  AMAZONPAY = "AmazonPay",
  GOOGLEPAY = "GooglePay",
  APPLEPAY = "ApplePay",
}
export interface IIsVisible {
  isVisible: boolean;
}

export const scrollToTop = (): void => {
  const sections = document.querySelectorAll("#ModalRootElement > section");
  if (sections.length) {
    sections.forEach((section) => (section.scrollTop = 0));
  }
};

export const DEFAULT_ERROR_MESSAGE = "default error message";
